import Container from '@brands/Components/Container/Container';
import { useWindowSize } from '@brands/hooks';
import { verifyEmailAddress } from '@brands/services/identity/verifyEmailAddress';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { formatEmail } from '@brands/Utils/formatDate';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import zIndex from '@mui/material/styles/zIndex';
import { MuiOtpInput } from 'mui-one-time-password-input';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AiOutlineClose } from 'react-icons/ai';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import Modal from 'react-modal';
import * as Yup from 'yup';

import styles from './styles.module.scss';

interface VerifyEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

interface VerifyEmailForm {
  code: string;
}

const VerifyEmailModal: FC<VerifyEmailModalProps> = ({ isOpen, onClose, email }) => {
  const isMobile = useWindowSize().width < 768;

  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required('Verification Code is required')
      .matches(/^\d{6}$/, 'The code must be a 6-digit number.'),
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    control,
  } = useForm<VerifyEmailForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: VerifyEmailForm): Promise<void> => {
    try {
      await verifyEmailAddress(data.code);
      onClose();

      window.location.reload();
    } catch (err) {
      displayErrorDetails(err);
    }
  };

  if (isMobile) {
    return (
      <Container
        patientDashboard
        isSidenavVisible
        childClassName={styles.verifyEmailDashboard}
        containerStyle={{
          justifyContent: 'start',
          overflowY: 'scroll',
          zIndex: zIndex.drawer + 1,
        }}
      >
        <div className={styles.verifyEmailDashboardContainer}>
          <div className={styles.verifyEmailCard}>
            <button type="button" className={styles.backButton} onClick={onClose} aria-label="Back">
              <MdKeyboardArrowLeft />
              Back
            </button>
            <div className={styles.verifyEmailContainer}>
              <p className={`fs-unmask ${styles.title}`}>Update email address</p>
              <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.welcomeTextContainer}>
                  <span className={`${styles.welcomeText} fs-unmask`}>
                    {`We sent a verification code to ${formatEmail(email)}`}
                  </span>
                </div>
                <div className={styles.inputWrap}>
                  <div className={styles.inputWrapper}>
                    <Controller
                      name="code"
                      control={control}
                      defaultValue=""
                      render={({}) => (
                        <MuiOtpInput
                          className="fs-exclude"
                          autoFocus
                          value={getValues('code')}
                          onChange={(value) => {
                            setValue('code', value);
                            if (value.length === 6) {
                              handleSubmit(onSubmit)();
                            }
                          }}
                          length={6}
                          TextFieldsProps={{
                            placeholder: '#',
                            inputMode: 'numeric',
                            type: 'tel',
                          }}
                          validateChar={(value) => {
                            return value.trim() !== '' && !isNaN(Number(value));
                          }}
                        />
                      )}
                    />

                    <div className={`${styles.invalidFeedback} fs-unmask`}>{errors.code && errors.code.message}</div>
                  </div>
                </div>
                <div className={styles.buttonContainer}>
                  <Button type="button" className={`fs-unmask ${styles.formCancelBtn}`} onClick={onClose}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Container>
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStylesDashboard}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      <div className={styles.verifyEmailCard}>
        <button
          type="button"
          className={`fs-unmask ${styles.modalClose}`}
          data-micromodal-close=""
          onClick={onClose}
          aria-label="Close modal"
        >
          <AiOutlineClose className={styles.icon} />
        </button>
        <button type="button" className={styles.backButton} onClick={onClose} aria-label="Back">
          <MdKeyboardArrowLeft />
          Back
        </button>
        <div className={styles.verifyEmailContainer}>
          <p className={styles.title}>Verify new email address</p>
          <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.welcomeTextContainer}>
              <span className={`${styles.welcomeText} fs-unmask`}>
                {`We sent a verification code to ${formatEmail(email)}`}
              </span>
            </div>
            <div className={styles.inputWrap}>
              <div className={styles.inputWrapper}>
                <Controller
                  name="code"
                  control={control}
                  defaultValue=""
                  render={({}) => (
                    <MuiOtpInput
                      className="fs-exclude"
                      autoFocus
                      value={getValues('code')}
                      onChange={(value) => {
                        setValue('code', value);
                        if (value.length === 6) {
                          handleSubmit(onSubmit)();
                        }
                      }}
                      length={6}
                      TextFieldsProps={{
                        placeholder: '#',
                        inputMode: 'numeric',
                        type: 'tel',
                      }}
                      validateChar={(value) => {
                        return value.trim() !== '' && !isNaN(Number(value));
                      }}
                    />
                  )}
                />

                <div className={`${styles.invalidFeedback} fs-unmask`}>{errors.code && errors.code.message}</div>
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <Button type="button" className={styles.formCancelBtn} onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default VerifyEmailModal;
